import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | valid | invalid | done
  const [who, setWho] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [invalidMsg, setInvalidMsg] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/auth/reset-password/${token}`)
      .then((r) => { if (alive) { setWho(r.data); setState('valid'); } })
      .catch((err) => {
        if (alive) {
          setInvalidMsg(errText(err, 'This reset link is no longer valid.'));
          setState('invalid');
        }
      });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post(`/auth/reset-password/${token}`, { password });
      setState('done');
      toast.success('Password updated');
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      toast.error(errText(err, 'Could not reset the password.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen hero-wash">
      <div className="noise min-h-screen flex flex-col">
        <header className="h-14 flex items-center px-4 lg:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="reset-logo">
            <div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">M</div>
            <span className="font-semibold tracking-tight">DoctorPaperwork</span>
          </Link>
        </header>
        <div className="flex-1 grid place-items-center px-4 py-10">
          <Card className="w-full max-w-md shadow-lg shadow-black/5" data-testid="reset-password-card">
            {state === 'checking' && (
              <CardContent className="pt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking your link…
              </CardContent>
            )}
            {state === 'invalid' && (
              <CardContent className="pt-6 text-center space-y-3" data-testid="reset-invalid-state">
                <div className="mx-auto h-12 w-12 rounded-full bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] grid place-items-center">
                  <AlertTriangle className="h-6 w-6" />
                </div>
                <h2 className="text-lg font-semibold">Link expired or already used</h2>
                <p className="text-sm text-muted-foreground">{invalidMsg}</p>
                <Button asChild variant="outline">
                  <Link to="/forgot-password" data-testid="reset-request-new-link">Request a new link</Link>
                </Button>
              </CardContent>
            )}
            {state === 'valid' && (
              <>
                <CardHeader>
                  <CardTitle className="text-xl">Choose a new password</CardTitle>
                  <CardDescription data-testid="reset-account-hint">
                    For {who?.name} ({who?.email_masked})
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="reset-password" className="text-xs font-medium">New password</Label>
                      <Input id="reset-password" type="password" required autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" data-testid="reset-password-input" />
                      <p className="text-xs text-muted-foreground">At least 10 characters with a letter and a number.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="reset-confirm" className="text-xs font-medium">Confirm new password</Label>
                      <Input id="reset-confirm" type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••••" data-testid="reset-confirm-input" />
                    </div>
                    <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit-button">
                      {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Set new password
                    </Button>
                  </form>
                </CardContent>
              </>
            )}
            {state === 'done' && (
              <CardContent className="pt-6 text-center space-y-3" data-testid="reset-done-state">
                <div className="mx-auto h-12 w-12 rounded-full bg-accent text-accent-foreground grid place-items-center">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <h2 className="text-lg font-semibold">Password updated</h2>
                <p className="text-sm text-muted-foreground">All previous sessions were signed out. Redirecting you to sign in…</p>
                <Button asChild>
                  <Link to="/login" data-testid="reset-go-login">Sign in now</Link>
                </Button>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
